// ===========================================
// TIPOS: Calendario (CORE)
// ===========================================

// Re-export tipos de vencimientos
export type {
  VencimientoFiscal,
  VencimientoConCliente,
  EstadisticasVencimientos,
  VencimientosDelDia,
  ResultadoGeneracionVencimientos,
} from '@/types/vencimientos'

export type { MarcarPresentadoData } from '@/core/components/calendario/MarcarPresentadoDialog'

// ===========================================
// Tipos de Eventos
// ===========================================

export type TipoEvento = 'vencimiento' | 'audiencia' | 'reunion' | 'recordatorio' | 'cita' | 'otro'
export type EstadoEvento = 'pendiente' | 'confirmado' | 'cancelado' | 'completado'

export interface EventoCalendario {
  id: string
  organizacion_id: string | null
  titulo: string
  descripcion: string | null
  tipo: TipoEvento
  fecha_inicio: string
  fecha_fin: string | null
  todo_el_dia: boolean
  recurrente: boolean
  regla_recurrencia: string | null
  vinculado_tipo: string | null
  vinculado_id: string | null
  workspace: string | null
  estado: EstadoEvento
  color: string | null
  ubicacion: string | null
  participantes: string[] | null
  notas: string | null
  created_by: string | null
  created_at: string
  updated_at: string
  // ORR-227 fase 3: resultado de crear la reunión en el Calendar del estudio.
  // meet_url en null = la reunión existe, el link todavía no.
  meet_url: string | null
  google_event_id: string | null
  google_sync_error: string | null
  /**
   * ORR-383. Solo presentes cuando la fila viene de expandir una serie: son la fecha de ESA
   * ocurrencia, para mostrarla. `fecha_inicio` sigue siendo la del maestro a propósito — es el
   * ancla de la serie, y pisarla haría que editar el título de una semana reancle todo.
   */
  ocurrencia_inicio?: string
  ocurrencia_fin?: string | null
}

export interface EventoCalendarioInsert {
  organizacion_id?: string | null
  titulo: string
  descripcion?: string | null
  tipo: TipoEvento
  fecha_inicio: string
  fecha_fin?: string | null
  todo_el_dia?: boolean
  recurrente?: boolean
  regla_recurrencia?: string | null
  vinculado_tipo?: string | null
  vinculado_id?: string | null
  workspace?: string
  estado?: EstadoEvento
  color?: string | null
  ubicacion?: string | null
  participantes?: string[] | null
  notas?: string | null
  created_by?: string | null
}

export interface EventoCalendarioUpdate {
  titulo?: string
  descripcion?: string | null
  tipo?: TipoEvento
  fecha_inicio?: string
  fecha_fin?: string | null
  todo_el_dia?: boolean
  recurrente?: boolean
  regla_recurrencia?: string | null
  vinculado_tipo?: string | null
  vinculado_id?: string | null
  workspace?: string
  estado?: EstadoEvento
  color?: string | null
  ubicacion?: string | null
  participantes?: string[] | null
  notas?: string | null
  // Sin `meet_url` ni `google_event_id`: los guarda solo la function de Google y la base se los
  // niega al cliente (ORR-537). El error sí, porque un fallo de red nunca llega a la function.
  google_sync_error?: string | null
}

// ===========================================
// Tipos Unificados (Eventos + Vencimientos)
// ===========================================

// `tarea_proyectada` (ORR-331) es una ocurrencia futura de una recurrente que TODAVÍA NO existe
// como fila: la dibuja el calendario a partir de `proyectar_tareas_recurrentes`. Es un tipo aparte
// y no un flag sobre `tarea` a propósito — el compilador obliga a decidir qué hacer con ella en
// cada lugar que ramifica, y así una proyección no se cuela en un flujo que espera un id real.
export type TipoItemCalendario = 'vencimiento' | 'evento' | 'tarea' | 'tarea_proyectada'

/**
 * Una fila de `proyectar_tareas_recurrentes` (ORR-331): ocurrencia futura de una recurrente que
 * todavía no existe en `tareas`. No tiene `id` propio — no es una fila.
 */
export interface TareaProyectada {
  tarea_recurrente_id: string
  fecha: string
  titulo: string
  descripcion: string | null
  cliente_id: string | null
  asignado_a: string | null
  prioridad: string | null
  workspace: string | null
}

export interface ItemCalendario {
  id: string
  tipo: TipoItemCalendario
  titulo: string
  descripcion: string | null
  fecha: string
  fecha_fin: string | null
  todo_el_dia: boolean
  estado: string
  color: string | null
  workspace: string | null
  // Para vencimientos
  cliente_id?: string | null
  cliente_nombre?: string | null
  tipo_impuesto?: string | null
  periodo?: string | null
  // Para eventos
  ubicacion?: string | null
  participantes?: string[] | null
  tipo_evento?: TipoEvento | null
  meet_url?: string | null
  // Para tareas (ORR-228)
  asignado_a?: string | null
  prioridad?: string | null
  // Datos originales
  datos_originales: EventoCalendario | Record<string, unknown>
}

export interface ItemsDelDia {
  fecha: string
  items: ItemCalendario[]
  totalVencimientos: number
  totalEventos: number
  totalTareas: number
  totalPendientes: number
}

// ===========================================
// Filtros
// ===========================================

export interface FiltrosVencimientos {
  tipos_impuesto?: string[]
  estados?: string[]
  responsables?: string[]
  clientes_ids?: string[]
  busqueda?: string
  desde?: string
  hasta?: string
  workspace?: string
}

export interface FiltrosEventos {
  organizacion_id?: string
  tipo?: TipoEvento
  estado?: EstadoEvento
  desde?: string
  hasta?: string
  workspace?: string
  vinculado_tipo?: string
  vinculado_id?: string
}

export interface FiltrosCalendario {
  desde?: string
  hasta?: string
  workspace?: string
  incluir_vencimientos?: boolean
  incluir_eventos?: boolean
  incluir_tareas?: boolean
  tipos_evento?: TipoEvento[]
  tipos_impuesto?: string[]
}

// ===========================================
// Configuración Visual
// ===========================================

export const TIPO_EVENTO_CONFIG: Record<TipoEvento, { label: string; color: string; icono: string }> = {
  vencimiento: {
    label: 'Vencimiento',
    color: '#EF4444',
    icono: 'calendar-clock',
  },
  audiencia: {
    label: 'Audiencia',
    color: '#8B5CF6',
    icono: 'gavel',
  },
  reunion: {
    label: 'Reunión',
    color: '#3B82F6',
    icono: 'users',
  },
  recordatorio: {
    label: 'Recordatorio',
    color: '#F59E0B',
    icono: 'bell',
  },
  cita: {
    label: 'Cita',
    color: '#10B981',
    icono: 'calendar-check',
  },
  otro: {
    label: 'Otro',
    color: '#6B7280',
    icono: 'calendar',
  },
}

export const ESTADO_EVENTO_CONFIG: Record<EstadoEvento, { label: string; color: string }> = {
  pendiente: {
    label: 'Pendiente',
    color: '#F59E0B',
  },
  confirmado: {
    label: 'Confirmado',
    color: '#3B82F6',
  },
  cancelado: {
    label: 'Cancelado',
    color: '#EF4444',
  },
  completado: {
    label: 'Completado',
    color: '#10B981',
  },
}

// Colores para el calendario (predefinidos para UI)
export const COLORES_CALENDARIO = [
  '#EF4444', // Rojo
  '#F59E0B', // Naranja
  '#10B981', // Verde
  '#3B82F6', // Azul
  '#8B5CF6', // Violeta
  '#EC4899', // Rosa
  '#6B7280', // Gris
]

/**
 * Un evento del calendario del estudio que se pisa con el horario que se está agendando
 * (ORR-301). Espeja `EventoOcupado` de `netlify/functions/utils/google-calendar-helpers.ts`:
 * están duplicados porque esbuild no resuelve el alias `@/` al bundlear las functions, la
 * misma razón por la que allá se duplica la regex de `parseFechaEvento`.
 */
export interface EventoOcupado {
  titulo: string
  inicio: string
  fin: string
}

/**
 * Respuesta de un invitado a una reunión, según Google Calendar (ORR-544).
 * Espeja `EstadoInvitado` de `netlify/functions/utils/google-calendar-helpers.ts`.
 */
export type EstadoInvitado = 'aceptado' | 'rechazado' | 'tal_vez' | 'sin_respuesta'

export interface InvitadoEnGoogle {
  email: string
  estado: EstadoInvitado
}

/**
 * Lo que Google Calendar dice de los invitados de una reunión, leído en `leidoAt`.
 * `enGoogle: false` es una reunión que Google no tiene o canceló.
 * `faltanEnGoogle` son los invitados de BrainHub que Google no tiene.
 */
export type InvitadosReunion =
  | { enGoogle: true; invitados: InvitadoEnGoogle[]; faltanEnGoogle: string[]; leidoAt: string }
  | { enGoogle: false; leidoAt: string }
